import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ChevronDown, Search } from 'lucide-react';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HelpFAQs = () => {
  const { t, i18n } = useTranslation();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/help/faqs`, { params: { language: i18n.language || 'es' } })
      .then(({ data }) => setFaqs(data))
      .catch(() => setFaqs([]))
      .finally(() => setLoading(false));
  }, [i18n.language]);

  const categories = ['all', ...Array.from(new Set(faqs.map(f => f.category)))];
  const categoryLabels = {
    all: t('help.faqs.catAll', 'Todas'),
    general: t('help.faqs.catGeneral', 'General'),
    getting_started: t('help.faqs.catStart', 'Primeros pasos'),
    contributors: t('help.faqs.catContributors', 'Colaboradores'),
    process: t('help.faqs.catProcess', 'Proceso'),
    ai: t('help.faqs.catAI', 'Inteligencia Artificial'),
    pricing: t('help.faqs.catPricing', 'Precios y pago'),
    content: t('help.faqs.catContent', 'Contenido del libro'),
    privacy: t('help.faqs.catPrivacy', 'Privacidad'),
    shipping: t('help.faqs.catShipping', 'Envíos'),
    support: t('help.faqs.catSupport', 'Soporte'),
  };

  const filtered = faqs.filter(f => {
    const matchesCat = activeCategory === 'all' || f.category === activeCategory;
    const q = search.toLowerCase();
    const matchesSearch = !q ||
      f.question.toLowerCase().includes(q) ||
      f.answer.toLowerCase().includes(q);
    return matchesCat && matchesSearch;
  });

  return (
    <div className="space-y-5" data-testid="help-faqs">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('help.faqs.searchPlaceholder', 'Buscar en FAQs...')}
          className="pl-10"
          data-testid="faqs-search"
        />
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2">
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            data-testid={`faq-cat-${cat}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              activeCategory === cat
                ? 'bg-slate-800 text-white shadow-sm'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {categoryLabels[cat] || cat}
          </button>
        ))}
      </div>

      {/* FAQs list */}
      {loading ? (
        <div className="text-center text-slate-500 py-8">…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-slate-500 py-10">
          {t('help.faqs.noResults', 'No hay resultados para tu búsqueda')}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((f, i) => (
            <div key={f.faq_id} className="border border-slate-200 rounded-xl bg-white overflow-hidden transition-shadow hover:shadow-sm">
              <button
                onClick={() => setOpenIdx(openIdx === f.faq_id ? null : f.faq_id)}
                className="w-full flex justify-between items-center text-left p-4 hover:bg-slate-50"
                data-testid={`faq-question-${i}`}
              >
                <span className="font-medium text-slate-800 pr-4">{f.question}</span>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 text-slate-400 transition-transform ${openIdx === f.faq_id ? 'rotate-180' : ''}`} />
              </button>
              {openIdx === f.faq_id && (
                <div className="px-4 pb-4 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-3 bg-slate-50/50" data-testid={`faq-answer-${i}`}>
                  {f.answer.split('\n').map((line, li) => (
                    <p key={li} className={li > 0 ? 'mt-2' : ''}>{line}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HelpFAQs;
